/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {completeCopyModal} from '../common/util/complete-copy-modal';
import {finalize, Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import {nextCopyTitle} from '../../components/util/next-copy-title';
import {Uuid} from '../../components/util/uuid';
import {EarningsAccountApiService, EarningsAccountModel} from './earnings-account-api.service';
import {deferViewWrite} from '../common/util/template-state';
import {FormsModule} from '@angular/forms';
import {AppSharedModule} from '../common/app-shared.module';

export interface EarningsAccountCopyResult {
  model: EarningsAccountModel;
  destination: boolean;
}

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule],
  selector: 'adn-earnings-account-copy-modal',
  template: `
    <form class="form-horizontal" (ngSubmit)="copy()">
      <div class="modal-header dialogAddStuff">
        <button type="button" class="close" (click)="dismiss()">&times;</button>
        <h4 class="modal-title">
          <span>{{ 'label.copy.earningsAcccount' | ogTranslate }}</span>
          <small>{{ originalName }}</small>
        </h4>
      </div>
      <div class="modal-body">
        @if (copyFailed) {
          <p class="alert alert-danger">The earnings account could not be copied. Try again.</p>
        }
        <div class="form-group">
          <label class="col-md-3 control-label">{{ 'label.name' | ogTranslate }}</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="earningsAcccount.name" [(ngModel)]="name" required>
          </div>
        </div>
        <div class="form-group">
          <label class="col-md-3 control-label">{{ 'label.description' | ogTranslate }}</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="earningsAcccount.description" [(ngModel)]="description">
          </div>
        </div>
        <div class="checkbox text-right">
          <label class="checkbox">
            <input type="checkbox" [(ngModel)]="destination" name="earningsAcccount.destination">
            <span class="small">Go to new report schedule after copy</span>
          </label>
        </div>
      </div>
      <div class="modal-footer">
        <a class="btn btn-link btn-xs" (click)="dismiss()">{{ 'button.cancel' | ogTranslate }}</a>
        <button type="submit" class="btn btn-default" [disabled]="pending">{{ 'label.copy' | ogTranslate }}</button>
      </div>
    </form>
  `
})
export class EarningsAccountCopyModalComponent implements OnInit {
  model: EarningsAccountModel;

  readonly onCopied = new Subject<EarningsAccountCopyResult>();

  originalName = '';
  name = '';
  description = '';
  destination = true;
  pending = false;
  copyFailed = false;

  constructor(
    public bsModalRef: BsModalRef,
    private readonly api: EarningsAccountApiService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.originalName = this.model?.name || '';
    this.name = nextCopyTitle(this.model?.name || '');
    this.description = this.model?.description || '';
  }

  copy(): void {
    if (this.pending) {
      return;
    }
    const copy: EarningsAccountModel = _.cloneDeep({...this.model,
      id: Uuid.generate(),
      name: this.name,
      description: this.description || null
    });
    this.pending = true;
    this.copyFailed = false;
    this.api.save(copy).pipe(
      finalize(() => deferViewWrite(this.cdr, () => this.pending = false))
    ).subscribe({
      next: (saved) => {
        completeCopyModal(this.bsModalRef, this.onCopied, {model: saved, destination: this.destination});
      },
      error: () => deferViewWrite(this.cdr, () => this.copyFailed = true)
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
